/**
 * Data Hub — Phase 1 fixtures.
 *
 * Generated deterministically in code rather than checked in as captured JSON.
 * Two reasons: the console needs 1,000+ coherent rows that would bloat the repo,
 * and — more importantly — the reference app holds real customer data. Every
 * account name below is fictional. Metric names are generic KPI vocabulary, not
 * customer data.
 *
 * Phase 2+ replaces these via `adapter.ts`; no tab component imports this file.
 */
import type {
  BenchmarkMetric,
  CategoryScores,
  DataHubStats,
  Matrix,
  MatrixMetric,
  PipelineRun,
  ProductionRow,
  QcProbe,
  QcReport,
  RoAccountPoint,
  RoScores,
  StagingRow,
  StageState,
} from "./types";

/* ---------------- deterministic PRNG ---------------- */

/** mulberry32 — stable across reloads so the UI never reshuffles under you. */
function makeRng(seed: number) {
  let a = seed;
  return () => {
    a |= 0;
    a = (a + 0x6d2b79f5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

const rand = makeRng(20260730);
const pick = <T>(xs: readonly T[]) => xs[Math.floor(rand() * xs.length)];
const between = (lo: number, hi: number) => lo + rand() * (hi - lo);
const round = (n: number, dp = 1) => Number(n.toFixed(dp));

/* ---------------- catalog ---------------- */

/** Fictional. Any resemblance to a real customer is not intended. */
export const ACCOUNTS = [
  "Northwind Trading",
  "Contoso Systems",
  "Fabrikam Industries",
  "Adventure Works",
  "Litware Financial",
  "Proseware Health",
  "Tailspin Logistics",
  "Wingtip Manufacturing",
  "Woodgrove Bank",
  "Lamna Insurance",
  "Trey Research",
  "Fourth Coffee",
  "Graphic Design Institute",
  "Alpine Ski House",
  "Blue Yonder Airlines",
  "Relecloud Media",
];

export const CATEGORIES = [
  "Adoption",
  "Engagement",
  "Experience",
  "Financial",
  "Health",
  "Support",
] as const;

export const METRICS: MatrixMetric[] = [
  {
    metric_id: "licenses_active_pct",
    metric_name: "% Licenses Active",
    category: "Adoption",
    uom: "%",
  },
  {
    metric_id: "licenses_provisioned_pct",
    metric_name: "% Licenses Provisioned",
    category: "Adoption",
    uom: "%",
  },
  {
    metric_id: "onboarding_completion_pct",
    metric_name: "Onboarding Completion %",
    category: "Adoption",
    uom: "%",
  },
  {
    metric_id: "avg_session_length",
    metric_name: "Average Session Length",
    category: "Engagement",
    uom: "Minutes",
  },
  {
    metric_id: "customer_response_time_avg",
    metric_name: "Customer Response Time",
    category: "Engagement",
    uom: "Hours",
  },
  {
    metric_id: "daily_active_users",
    metric_name: "Daily Active Users",
    category: "Engagement",
    uom: "Count",
  },
  {
    metric_id: "feature_usage_engagement",
    metric_name: "Feature Usage Engagement",
    category: "Engagement",
    uom: "%",
  },
  {
    metric_id: "repeat_users_pct",
    metric_name: "Repeat Users Percent",
    category: "Engagement",
    uom: "%",
  },
  {
    metric_id: "ttfv",
    metric_name: "Time to First Value (TTFV)",
    category: "Engagement",
    uom: "Days",
  },
  {
    metric_id: "csat",
    metric_name: "Customer Sat Score (CSAT)",
    category: "Experience",
    uom: "Score",
  },
  {
    metric_id: "nps",
    metric_name: "Net Promoter Score (NPS)",
    category: "Experience",
    uom: "Score",
  },
  {
    metric_id: "arr",
    metric_name: "Annual Recurring Revenue (ARR)",
    category: "Financial",
    uom: "USD",
  },
  {
    metric_id: "clv_cac_ratio",
    metric_name: "CLV to CAC Ratio",
    category: "Financial",
    uom: "Ratio",
  },
  {
    metric_id: "cac",
    metric_name: "Customer Acquisition Cost (CAC)",
    category: "Financial",
    uom: "USD",
  },
  {
    metric_id: "churn_rate_arr",
    metric_name: "Customer Churn Rate - ARR",
    category: "Financial",
    uom: "%",
  },
  {
    metric_id: "clv",
    metric_name: "Customer Lifetime Value (CLV)",
    category: "Financial",
    uom: "USD",
  },
  {
    metric_id: "expansion_rate",
    metric_name: "Expansion Rate",
    category: "Financial",
    uom: "%",
  },
  {
    metric_id: "grr",
    metric_name: "Gross Revenue Retention (GRR)",
    category: "Financial",
    uom: "%",
  },
  {
    metric_id: "logo_retention_rate",
    metric_name: "Logo Retention Rate",
    category: "Financial",
    uom: "%",
  },
  {
    metric_id: "mrr",
    metric_name: "Monthly Recurring Revenue (MRR)",
    category: "Financial",
    uom: "USD",
  },
  {
    metric_id: "nrr",
    metric_name: "Net Revenue Retention (NRR)",
    category: "Financial",
    uom: "%",
  },
  {
    metric_id: "customer_health_score",
    metric_name: "Customer Health Score",
    category: "Health",
    uom: "Score",
  },
  {
    metric_id: "customer_risk_score",
    metric_name: "Customer Risk Score",
    category: "Health",
    uom: "Score",
  },
  {
    metric_id: "platform_dependency_score",
    metric_name: "Platform Dependency Score",
    category: "Health",
    uom: "Score",
  },
  {
    metric_id: "relationship_health_score",
    metric_name: "Relationship Health Score",
    category: "Health",
    uom: "Score",
  },
  {
    metric_id: "strategic_value_score",
    metric_name: "Strategic Value Score",
    category: "Health",
    uom: "Score",
  },
  {
    metric_id: "ticket_resolution_time",
    metric_name: "Avg Ticket Resolution Time",
    category: "Support",
    uom: "Hours",
  },
  {
    metric_id: "escalations_trend_30d",
    metric_name: "Escalation Trend (30d Delta)",
    category: "Support",
    uom: "Count",
  },
  {
    metric_id: "escalations_count_30d",
    metric_name: "Escalations Count (Last 30 Days)",
    category: "Support",
    uom: "Count",
  },
  {
    metric_id: "open_tickets",
    metric_name: "Open Tickets",
    category: "Support",
    uom: "Count",
  },
  {
    metric_id: "ticket_volume",
    metric_name: "Ticket Volume",
    category: "Support",
    uom: "Count",
  },
];

const LOWER_IS_BETTER = new Set([
  "cac",
  "churn_rate_arr",
  "ttfv",
  "customer_response_time_avg",
  "ticket_resolution_time",
  "escalations_trend_30d",
  "escalations_count_30d",
  "open_tickets",
  "ticket_volume",
  "customer_risk_score",
]);

const DATES = ["2026-03-30", "2026-04-30", "2026-05-12", "2026-06-05"];
const PERIODS = ["Mar, 2026", "Apr, 2026", "May, 2026", "Jun, 2026"];
/** 12 monthly buckets for composite / R&O trend charts (period_start ISO). */
const TREND_PERIOD_STARTS = [
  "2025-07-01",
  "2025-08-01",
  "2025-09-01",
  "2025-10-01",
  "2025-11-01",
  "2025-12-01",
  "2026-01-01",
  "2026-02-01",
  "2026-03-01",
  "2026-04-01",
  "2026-05-01",
  "2026-06-01",
];

/* ---------------- stats ---------------- */

export const STATS: DataHubStats = {
  staging: 1990,
  timeseries: 1984,
  benchmarks: 279,
  summaries: 5456,
  runs: 4,
};

/* ---------------- pipeline runs ---------------- */

export const PIPELINE_RUNS: PipelineRun[] = [
  {
    id: "run-4",
    dataset_kind: "production",
    run_no: 4,
    started_at: "2026-07-29T14:22:04Z",
    finished_at: "2026-07-29T14:24:51Z",
    duration_ms: 167_000,
    status: "partial",
    source: "metrics_import_sample.xlsx",
    triggered_by: "ops@example.com",
    phases: [
      { phase: "import", status: "success", count: 1990 },
      { phase: "cleanse", status: "success", count: 41 },
      { phase: "commit", status: "success", count: 1949 },
      {
        phase: "benchmark",
        status: "failed",
        count: 0,
        error: "insufficient accounts for 4 metrics",
      },
      {
        phase: "normalize",
        status: "skipped",
        reason: "prerequisite benchmark did not succeed",
      },
      {
        phase: "summarize",
        status: "skipped",
        reason: "prerequisite normalize did not succeed",
      },
      {
        phase: "kpi_scores",
        status: "skipped",
        reason: "prerequisite normalize did not succeed",
      },
      {
        phase: "ro_scores",
        status: "skipped",
        reason: "prerequisite normalize did not succeed",
      },
    ],
  },
  {
    id: "run-3",
    dataset_kind: "production",
    run_no: 3,
    started_at: "2026-07-28T09:10:12Z",
    finished_at: "2026-07-28T09:14:38Z",
    duration_ms: 266_000,
    status: "succeeded",
    source: "metrics_import_sample.xlsx",
    triggered_by: "ops@example.com",
    phases: [
      { phase: "import", status: "success", count: 1990 },
      { phase: "cleanse", status: "success", count: 38 },
      { phase: "commit", status: "success", count: 1952 },
      { phase: "benchmark", status: "success", count: 279 },
      { phase: "normalize", status: "success", count: 1952 },
      { phase: "summarize", status: "success", count: 5456 },
      { phase: "kpi_scores", status: "success", count: 448 },
      { phase: "ro_scores", status: "success", count: 84 },
    ],
  },
  {
    id: "run-2",
    dataset_kind: "synthetic",
    run_no: 2,
    started_at: "2026-07-21T16:02:44Z",
    finished_at: "2026-07-21T16:03:02Z",
    duration_ms: 18_000,
    status: "failed",
    source: "q2-refresh.csv",
    triggered_by: "ops@example.com",
    phases: [
      {
        phase: "import",
        status: "failed",
        count: 0,
        error: "column 'measurement_date' missing from header row",
      },
      {
        phase: "cleanse",
        status: "skipped",
        reason: "prerequisite import did not succeed",
      },
      {
        phase: "commit",
        status: "skipped",
        reason: "prerequisite import did not succeed",
      },
    ],
  },
  {
    id: "run-1",
    dataset_kind: "production",
    run_no: 1,
    started_at: "2026-07-14T11:40:00Z",
    finished_at: "2026-07-14T11:45:19Z",
    duration_ms: 319_000,
    status: "succeeded",
    source: "initial-load.xlsx",
    triggered_by: "ops@example.com",
    phases: [
      { phase: "import", status: "success", count: 1876 },
      { phase: "cleanse", status: "success", count: 96 },
      { phase: "commit", status: "success", count: 1780 },
      { phase: "benchmark", status: "success", count: 262 },
      { phase: "normalize", status: "success", count: 1780 },
      { phase: "summarize", status: "success", count: 5120 },
      { phase: "kpi_scores", status: "success", count: 420 },
      { phase: "ro_scores", status: "success", count: 78 },
    ],
  },
];

/* ---------------- staging rows ---------------- */

function makeStagingRows(n: number): StagingRow[] {
  const rows: StagingRow[] = [];
  for (let i = 0; i < n; i++) {
    const m = pick(METRICS);
    const roll = rand();
    const bucket =
      roll < 0.62
        ? "promoted"
        : roll < 0.74
          ? "fixed"
          : roll < 0.84
            ? "open"
            : roll < 0.9
              ? "deduped"
              : roll < 0.96
                ? "ignored"
                : "rejected";
    const rejected = bucket === "rejected";
    const state: StageState =
      bucket === "promoted"
        ? "production.promoted"
        : bucket === "fixed"
          ? "staging.fixed"
          : bucket === "deduped"
            ? "staging.deduped"
            : bucket === "ignored"
              ? "staging.ignored"
              : "staging.open";
    const accountName = pick(ACCOUNTS);
    rows.push({
      id: `stg-${i + 1}`,
      // Fixtures model an already-resolved row; the source columns mirror what
      // a real import would have carried in.
      account_id: `acct-${i + 1}`,
      account_name: accountName,
      source_account_id: `SFDC-${String(i + 1).padStart(4, "0")}`,
      source_account_name: accountName,
      metric_id: m.metric_id,
      metric_name: m.metric_name,
      category: m.category,
      measurement_date: pick(DATES),
      value: round(between(1, 980), 1),
      lifecycle_status: rejected ? "rejected" : "accepted",
      stage_state: state,
      pipeline_status:
        bucket === "promoted"
          ? "staged"
          : bucket === "rejected"
            ? "rejected"
            : "pending",
      qc: {
        bucket,
        findings:
          bucket === "open"
            ? [
                {
                  probe_id: "VALUE_IN_RANGE",
                  severity: "warn",
                  message: "value outside expected envelope",
                },
              ]
            : bucket === "rejected"
              ? [
                  {
                    probe_id: "RESOLVE_ACCOUNT",
                    severity: "hard",
                    message: "account could not be resolved",
                  },
                ]
              : [],
      },
      created_at: "2026-07-29T14:22:04Z",
    });
  }
  return rows;
}

export const STAGING_ROWS = makeStagingRows(1000);

/* ---------------- production rows ---------------- */

function makeProductionRows(n: number): ProductionRow[] {
  const rows: ProductionRow[] = [];
  for (let i = 0; i < n; i++) {
    const m = METRICS[i % METRICS.length];
    const account = ACCOUNTS[Math.floor(i / METRICS.length) % ACCOUNTS.length];
    const value =
      m.uom === "USD"
        ? Math.round(between(8_000, 950_000))
        : m.uom === "%"
          ? round(between(4, 99))
          : Math.round(between(3, 260));
    rows.push({
      id: `prod-${i + 1}`,
      measurement_date: DATES[i % DATES.length],
      account_name: account,
      metric_name: m.metric_name,
      metric_id: m.metric_id,
      category: m.category,
      value,
      normalized_value: round(between(22, 97)),
    });
  }
  return rows;
}

export const PRODUCTION_ROWS = makeProductionRows(1000);

/* ---------------- QC ---------------- */

export const QC_PROBES: QcProbe[] = [
  {
    id: "RESOLVE_ORG",
    group: "resolution",
    label: "Resolve organization",
    description: "Attach the row to the active organization.",
    severity: "auto-fix",
    default_enabled: true,
  },
  {
    id: "RESOLVE_ACCOUNT",
    group: "resolution",
    label: "Resolve account",
    description: "Account name must match a known account.",
    severity: "hard",
    default_enabled: true,
  },
  {
    id: "RESOLVE_METRIC",
    group: "resolution",
    label: "Resolve metric",
    description: "Metric must exist in the definitions catalog.",
    severity: "hard",
    default_enabled: true,
  },
  {
    id: "RESOLVE_DATE",
    group: "resolution",
    label: "Resolve date",
    description: "Measurement date must parse to a real date.",
    severity: "hard",
    default_enabled: true,
  },
  {
    id: "TRIM_WHITESPACE",
    group: "hygiene",
    label: "Trim whitespace",
    description: "Strip leading and trailing whitespace.",
    severity: "auto-fix",
    default_enabled: true,
  },
  {
    id: "FILL_FROM_METRIC_DEFINITION",
    group: "hygiene",
    label: "Fill from definition",
    description: "Backfill UOM, scale and category from the metric definition.",
    severity: "auto-fix",
    default_enabled: true,
  },
  {
    id: "NORMALIZE_NUMERIC_FORMAT",
    group: "hygiene",
    label: "Normalize numeric format",
    description:
      "Strip thousands separators, currency symbols and trailing %, only when the result parses as a number.",
    severity: "auto-fix",
    default_enabled: true,
  },
  {
    id: "VALUE_PRESENT",
    group: "value_conformance",
    label: "Value present",
    description: "A measurement value is required.",
    severity: "hard",
    default_enabled: true,
  },
  {
    id: "VALUE_NUMERIC",
    group: "value_conformance",
    label: "Value numeric",
    description: "Value must be numeric after hygiene.",
    severity: "hard",
    default_enabled: true,
  },
  {
    id: "VALUE_IN_RANGE",
    group: "value_conformance",
    label: "Value in range",
    description: "Value falls inside the metric's expected envelope.",
    severity: "warn",
    default_enabled: true,
  },
  {
    id: "VALUE_SCALE_PLAUSIBILITY",
    group: "value_conformance",
    label: "Scale plausibility",
    description:
      "Proposes a rescale factor for a human; never rescales silently.",
    severity: "warn",
    default_enabled: true,
  },
  {
    id: "UOM_MATCHES_DEFINITION",
    group: "value_conformance",
    label: "UOM matches definition",
    description: "Unit of measure agrees with the metric definition.",
    severity: "warn",
    default_enabled: true,
  },
  {
    id: "SCALE_MATCHES_DEFINITION",
    group: "value_conformance",
    label: "Scale matches definition",
    description: "Scale agrees with the metric definition.",
    severity: "warn",
    default_enabled: true,
  },
  {
    id: "FORMAT_MATCHES_DEFINITION",
    group: "value_conformance",
    label: "Format matches definition",
    description: "Format agrees with the metric definition.",
    severity: "warn",
    default_enabled: true,
  },
  {
    id: "CATEGORY_MATCHES_DEFINITION",
    group: "value_conformance",
    label: "Category matches definition",
    description: "Category agrees with the metric definition.",
    severity: "warn",
    default_enabled: true,
  },
  {
    id: "DUPE_IN_STAGING",
    group: "duplicates",
    label: "Duplicate in staging",
    description:
      "Groups on account + metric + date; identical values auto-dedupe keeping latest, differing values raise an open issue.",
    severity: "hard",
    default_enabled: true,
  },
  {
    id: "DUPE_VS_TIMESERIES",
    group: "duplicates",
    label: "Duplicate vs production",
    description: "Row already exists in the production timeseries.",
    severity: "hard",
    default_enabled: true,
  },
];

export const QC_REPORTS: Record<string, QcReport> = {
  "run-4": {
    run_id: "run-4",
    generated_at: "2026-07-29T14:23:10Z",
    scope: "file",
    rows_scanned: 1990,
    rows_auto_fixed: 41,
    rows_open: 27,
    rows_rejected: 14,
    by_probe: [
      {
        probe_id: "TRIM_WHITESPACE",
        label: "Trim whitespace",
        group: "hygiene",
        severity: "auto-fix",
        count: 22,
      },
      {
        probe_id: "NORMALIZE_NUMERIC_FORMAT",
        label: "Normalize numeric format",
        group: "hygiene",
        severity: "auto-fix",
        count: 19,
      },
      {
        probe_id: "VALUE_IN_RANGE",
        label: "Value in range",
        group: "value_conformance",
        severity: "warn",
        count: 18,
      },
      {
        probe_id: "VALUE_SCALE_PLAUSIBILITY",
        label: "Scale plausibility",
        group: "value_conformance",
        severity: "warn",
        count: 9,
      },
      {
        probe_id: "RESOLVE_ACCOUNT",
        label: "Resolve account",
        group: "resolution",
        severity: "hard",
        count: 8,
      },
      {
        probe_id: "DUPE_IN_STAGING",
        label: "Duplicate in staging",
        group: "duplicates",
        severity: "hard",
        count: 6,
      },
    ],
  },
  "run-3": {
    run_id: "run-3",
    generated_at: "2026-07-28T09:11:40Z",
    scope: "file",
    rows_scanned: 1990,
    rows_auto_fixed: 38,
    rows_open: 12,
    rows_rejected: 5,
    by_probe: [
      {
        probe_id: "TRIM_WHITESPACE",
        label: "Trim whitespace",
        group: "hygiene",
        severity: "auto-fix",
        count: 20,
      },
      {
        probe_id: "NORMALIZE_NUMERIC_FORMAT",
        label: "Normalize numeric format",
        group: "hygiene",
        severity: "auto-fix",
        count: 18,
      },
      {
        probe_id: "VALUE_IN_RANGE",
        label: "Value in range",
        group: "value_conformance",
        severity: "warn",
        count: 12,
      },
      {
        probe_id: "RESOLVE_METRIC",
        label: "Resolve metric",
        group: "resolution",
        severity: "hard",
        count: 5,
      },
    ],
  },
};

/* ---------------- benchmarks ---------------- */

export const BENCHMARKS: BenchmarkMetric[] = METRICS.map((m, i) => {
  const base =
    m.uom === "USD"
      ? between(40_000, 600_000)
      : m.uom === "%"
        ? between(35, 80)
        : between(20, 140);
  const spread = base * between(0.35, 0.9);
  const min = Math.max(0, base - spread);
  const max = base + spread * between(1.2, 3.4);
  const q = (f: number) => round(min + (max - min) * f, 2);
  return {
    metric_id: m.metric_id,
    metric_name: m.metric_name,
    category: m.category,
    uom: m.uom,
    higher_is_better: !LOWER_IS_BETTER.has(m.metric_id),
    min: round(min, 2),
    p10: q(0.1),
    p25: q(0.25),
    p50: q(0.5),
    p75: q(0.75),
    p90: q(0.9),
    max: round(max, 2),
    mean: round(base, 2),
    stddev: round(spread * 0.55, 2),
    tier: (i % 7 === 0 ? 1 : i % 11 === 0 ? 0 : 2) as 0 | 1 | 2,
    source:
      i % 7 === 0
        ? "industry_default"
        : i % 11 === 0
          ? "seeded_rubric"
          : "org_calculated",
    confidence: i % 9 === 0 ? "medium" : i % 17 === 0 ? "low" : "high",
    dqs: round(between(62, 100), 2),
    n: 64,
  };
});

/* ---------------- matrices ---------------- */

function makeMatrix(rowKeys: string[], rowLabels: string[]): Matrix {
  return {
    metrics: METRICS,
    rows: rowKeys.map((key, r) => ({
      key,
      label: rowLabels[r],
      cells: Object.fromEntries(
        METRICS.map(m => {
          const missing = rand() < 0.04;
          return [
            m.metric_id,
            missing
              ? { normalized: null, actual: null }
              : {
                  normalized: round(between(26, 98)),
                  actual:
                    m.uom === "USD"
                      ? Math.round(between(8_000, 950_000))
                      : round(between(3, 240)),
                },
          ];
        })
      ),
    })),
  };
}

export const NORMALIZED_MATRIX = makeMatrix(DATES, DATES);
export const SUMMARIZED_MATRIX = makeMatrix(PERIODS, PERIODS);

/* ---------------- category scores ---------------- */

export const CATEGORY_SCORES: CategoryScores = {
  saved_at: "2026-07-28T23:16:08Z",
  category_rows: 448,
  overall_rows: 84,
  composite: 62.4,
  categories: [
    { category_key: "adoption", label: "Adoption", score: 66.1, max: 100 },
    { category_key: "engagement", label: "Engagement", score: 58.4, max: 100 },
    { category_key: "experience", label: "Experience", score: 71.2, max: 100 },
    { category_key: "financial", label: "Financial", score: 55.9, max: 100 },
    { category_key: "health", label: "Health", score: 63.8, max: 100 },
    { category_key: "support", label: "Support", score: 58.1, max: 100 },
  ],
  per_kpi: METRICS.map(m => ({
    metric_id: m.metric_id,
    metric_name: m.metric_name,
    category_key: m.category.toLowerCase(),
    effective_share: round(
      100 / METRICS.filter(x => x.category === m.category).length,
      1
    ),
    n: 64,
    score: round(between(38, 94)),
  })),
  trend: TREND_PERIOD_STARTS.map((p, i) => ({
    period_start: p,
    composite: round(52 + i * 0.9 + between(-1.5, 1.5)),
  })),
  // Phase 1 renders the banner slot empty. The reference app shows drift here;
  // see the plan's open items — EB should port a correct mapping, not the drift.
  label_drift: null,
};

/* ---------------- risk & opportunity ---------------- */

const RISK_DIMENSIONS = [
  { key: "D1", label: "Product Adoption & Engagement", share: 22.0, n: 256 },
  { key: "D2", label: "Customer Health & Risk", share: 20.0, n: 128 },
  { key: "D3", label: "Financial Retention Signals", share: 18.0, n: 256 },
  { key: "D4", label: "Support & Escalation Stress", share: 12.0, n: 256 },
  { key: "D5", label: "Customer Experience Sentiment", share: 12.0, n: 128 },
  {
    key: "D6",
    label: "Relationship Fragility",
    share: 10.0,
    n: 64,
    optional: true,
  },
  { key: "D7", label: "Organizational Instability", share: 6.0, n: 192 },
];

const OPP_DIMENSIONS = [
  { key: "O1", label: "Adoption Saturation & Headroom", share: 25.0, n: 128 },
  { key: "O2", label: "Engagement Depth & Growth", share: 20.0, n: 192 },
  { key: "O3", label: "Financial Expansion Momentum", share: 18.0, n: 128 },
  { key: "O4", label: "Customer Sentiment & Advocacy", share: 15.0, n: 128 },
  {
    key: "O5",
    label: "Relationship Leverage",
    share: 12.0,
    n: 64,
    optional: true,
  },
  { key: "O6", label: "Strategic & Platform Alignment", share: 6.0, n: 128 },
  {
    key: "O7",
    label: "Onboarding & Time-to-Value Quality",
    share: 4.0,
    n: 128,
  },
];

function quadrantOf(rcs: number, ocs: number): RoAccountPoint["quadrant"] {
  const highRisk = rcs < 50;
  const highOpp = ocs >= 50;
  if (!highRisk && highOpp) return "Q1"; // High opp, low risk — Invest & Grow
  if (highRisk && highOpp) return "Q2"; // High opp, high risk — Stabilise then Grow
  if (!highRisk && !highOpp) return "Q3"; // Low opp, low risk — Develop & Nurture
  return "Q4"; // Low opp, high risk — Defend & Remediate
}

const RO_ACCOUNTS: RoAccountPoint[] = ACCOUNTS.map((name, i) => {
  const rcs = round(between(38, 78));
  const ocs = round(between(34, 82));
  return {
    account_id: `acct-${i + 1}`,
    account_name: name,
    rcs,
    ocs,
    quadrant: quadrantOf(rcs, ocs),
  };
});

export const RO_SCORES: RoScores = {
  rcs: 60.5,
  ocs: 60.0,
  rcs_band: "R2 · Low",
  ocs_band: "O4 · High",
  risk_dimensions: RISK_DIMENSIONS.map(d => ({
    ...d,
    score: round(between(48, 88)),
  })),
  opportunity_dimensions: OPP_DIMENSIONS.map(d => ({
    ...d,
    score: round(between(46, 74)),
  })),
  accounts: RO_ACCOUNTS,
  trend: PERIODS.map((p, i) => ({
    period: p,
    rcs: round(57 + i * 1.1),
    ocs: round(56 + i * 1.3),
  })),
  deltas: PERIODS.slice(1).map((p, i) => ({
    label: `${PERIODS[i]} → ${p}`,
    rcs_delta: round(between(-3.5, 4.2)),
    ocs_delta: round(between(-2.8, 5.1)),
  })),
  dimension_deltas: [...RISK_DIMENSIONS, ...OPP_DIMENSIONS].map(d => ({
    key: d.key,
    label: d.label,
    delta: round(between(-6.5, 7.5)),
  })),
  top_movers: Array.from({ length: 8 }, () => ({
    account_name: pick(ACCOUNTS),
    metric: pick(METRICS).metric_name,
    delta: round(between(-18, 22)),
  })),
};
